import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Typography, IconButton, Paper, Radio, RadioGroup } from "@mui/material";
import * as Mui from "@mui/material";
import {
  Close,
  CloseFullscreen,
} from "@mui/icons-material";
import { CustomTextBox } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import { themes } from "services/constants";
import { useSelector } from "react-redux";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { CustomTimePicker } from "components/CustomComponents/CustomTimePicker";
import { CustomizedDatePicker } from "components/CustomComponents/CustomizedDatePicker";
import { otTimingOptions } from "pages/TeamZone/TeamZoneHeaders";
import {
  optionsStyle,
  cardStyle,
} from "pages/HR/Shift/Configuration/ShiftCategory/Component/ShiftCategoryOptions";
import dayjs from "dayjs";
import { useFetch } from "services/hooks/useFetch";
import {
  APP_BAR_OFFSET,
  designColors,
  designRadii,
  designShell,
  designType,
  FORM_ACTION_BUTTON_WIDTH,
  PANEL_DRAWER_WIDTH,
  panelDrawerSx,
} from "services/constants/designTokens";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import {
  GlobalFieldLabel,
  useExpandedContentLeft,
} from "components/DesignSystem/sharedUiPatterns";
import { SelectedEmployeeHeader } from "../SelectedEmployeeHeader";
import { OpenFullViewIcon } from "components/DesignSystem/OpenFullViewIcon";
import { tooltipStyleProps } from "services/constants/Styles";
import { PanelHeaderIconButton } from "components/DesignSystem/GlobalSidePanel";

// Create-only — this creates a Planned (Pre-Approval) overtime request.
// Completed (Post-Approval) records are system-derived from attendance and
// are never manually created here, matching the old NewPreApproval.js.
export const Apply = ({
  onClose,
  setReCallApi,
  open = true,
  expanded = false,
  onToggleExpand,
}) => {
  const {
    setValue,
    handleSubmit,
    register,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      user_name: "",
      start_date: null,
      end_date: null,
      applicable_timing: "Only Before",
      reason: "",
      button_loader: false,
    },
  });
  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);
  const { domain, userDetails, topbarCompanyId, userScreenAccess } =
    useSelector((state) => state.tokenReducer);
  const { result: users } = useFetch(
    `${domain}list/employee?user_id=${userDetails.id}&company_id=${topbarCompanyId}`,
  );
  // Editor-only submissions must stay pending; approval requires approver access.
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Overtime",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Overtime",
    );
  const contentLeft = useExpandedContentLeft(expanded);

  useEffect(() => {
    if (open) {
      reset();
      setShowAutoCompleteAvathar(false);
    }
  }, [open, reset]);

  const updateUserName = (event, data) => {
    if (data) {
      setValue("user_name", data);
      setValue("user_id", data?.user_id);
      setShowAutoCompleteAvathar(false);
    } else {
      setValue("user_name", null);
      setValue("user_id", "");
      setShowAutoCompleteAvathar(true);
    }
  };

  const handleApplicableTiming = (event) => {
    setValue("applicable_timing", event.target.value);
  };

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    const payload = {
      ...(!hasApproverAccess && { status: "Pending" }),
      user_id: data.user_id,
      ot_start_date: data.start_date?.format("YYYY-MM-DD"),
      ot_end_date: data.end_date?.format("YYYY-MM-DD"),
      ot_duration: dayjs(data.durationHrs).format("HH:mm"),
      ot_consideration: data.applicable_timing,
      reason: data.reason,
    };
    const response = await customAPI(
      `${domain}Team_Zone/Approve/Over_time/teamworkspace-ot-approve/?company_id=${topbarCompanyId}`,
      "POST",
      payload,
    );
    if (response?.ok) {
      showToast("success", response?.responseDetails?.message || "Overtime request created.");
      setReCallApi(true);
      reset();
      onClose();
      setValue("button_loader", false);
    } else {
      setValue("button_loader", false);
      showToast(
        "error",
        response?.message || "Couldn't create the overtime request. Please try again.",
      );
    }
  };

  const panelContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: expanded ? designRadii.card : 0,
        overflow: "hidden",
        backgroundColor: designColors.white,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          backgroundColor: designColors.white,
        }}
      >
        <Box sx={{ p: 3, pb: 0 }}>
          <Mui.Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{
              pb: 2.25,
              borderBottom: `1px solid ${designColors.slate200}`,
            }}
          >
            <Box>
              <Typography sx={designType.panelTitle}>New Overtime Request</Typography>
              <Typography sx={{ ...designType.pageSubtitle, mt: 0.5 }}>
                Create a planned overtime request for a selected period
              </Typography>
            </Box>
            <Mui.Stack direction="row" spacing={1} alignItems="center">
              {onToggleExpand ? (
                expanded ? (
                  <PanelHeaderIconButton
                    expanded
                    title="Collapse"
                    onClick={onToggleExpand}
                  >
                    <CloseFullscreen />
                  </PanelHeaderIconButton>
                ) : (
                  <Mui.Tooltip arrow slotProps={tooltipStyleProps} title="Open full view">
                    <IconButton
                      onClick={onToggleExpand}
                      size="small"
                      sx={{
                        ...designShell.panelSoftIconButton,
                        color: designColors.slate500,
                      }}
                    >
                      <OpenFullViewIcon fontSize="small" />
                    </IconButton>
                  </Mui.Tooltip>
                )
              ) : null}
              {expanded ? (
                <PanelHeaderIconButton
                  expanded
                  title="Close"
                  onClick={onClose}
                >
                  <Close />
                </PanelHeaderIconButton>
              ) : (
                <IconButton
                  onClick={onClose}
                  size="small"
                  sx={{
                    ...designShell.panelSoftIconButton,
                    color: designColors.slate500,
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </Mui.Stack>
          </Mui.Stack>
        </Box>
      </Paper>
      <Mui.Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            <Box>
              <GlobalFieldLabel helperText="Select the employee requesting or assigned to work overtime.">
                Employee Name
              </GlobalFieldLabel>
              <CustomAutoCompleteWithLogo
                dataList={users?.map((obj) => ({
                  ...obj,
                  label: obj.user_name,
                  image: obj?.image_url,
                }))}
                selectedValue={watch("user_name")}
                rules={{
                  ...register("user_name", { required: "Employee name is required" }),
                }}
                updateDataFunction={updateUserName}
                placeHolder="Select employee name"
                errorMessage={errors?.user_name?.message || ""}
                textBoxError={Boolean(errors.user_name)}
                showAvathar={showAutoCompleteAvathar}
                setShowAutoCompleteAvathar={setShowAutoCompleteAvathar}
              />
            </Box>
            <SelectedEmployeeHeader selectedUser={watch("user_name")} />
            <Box>
              <GlobalFieldLabel helperText="Select the start date for the overtime period">
                Start Date
              </GlobalFieldLabel>
              <CustomizedDatePicker
                name="start_date"
                control={control}
                required
                requiredMessage="Start date is required"
                placeholder="Select start date"
              />
            </Box>
            <Box>
              <GlobalFieldLabel helperText="Select the end date for the overtime period">
                End Date
              </GlobalFieldLabel>
              <CustomizedDatePicker
                name="end_date"
                control={control}
                required
                requiredMessage="End date is required"
                placeholder="Select end date"
                startDate={watch("start_date")}
              />
            </Box>
            <Box>
              <GlobalFieldLabel helperText="Enter the maximum allowed overtime hours as defined by the OT Policy.">
                Duration in Hrs
              </GlobalFieldLabel>
              <CustomTimePicker
                name="durationHrs"
                control={control}
                errors={errors}
                validateFn={(value) => {
                  if (!value || !dayjs(value).isValid()) {
                    return "Duration hours is required";
                  }
                  return true;
                }}
              />
            </Box>
            <Box>
              <GlobalFieldLabel helperText="Select when the overtime will be considered">
                Applicable Timing
              </GlobalFieldLabel>
              <RadioGroup
                row
                value={watch("applicable_timing")}
                onChange={handleApplicableTiming}
                sx={optionsStyle}
              >
                {otTimingOptions.map((opt) => {
                  const isActive = watch("applicable_timing") === opt.id;
                  return (
                    <Box key={opt.id} sx={cardStyle(isActive)}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Radio
                          value={opt.id}
                          sx={{
                            "&.Mui-checked": { color: themes.stepActiveColor },
                          }}
                        />
                        <Typography
                          fontWeight="medium"
                          sx={{ color: isActive ? themes.stepActiveColor : "inherit" }}
                        >
                          {opt.label}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color={designColors.slate600}
                        sx={{ ml: 4, maxWidth: 250, wordBreak: "break-word" }}
                      >
                        {opt.description}
                      </Typography>
                    </Box>
                  );
                })}
              </RadioGroup>
            </Box>
            <Box>
              <GlobalFieldLabel helperText="Mention the purpose or justification for the overtime request">
                Reason
              </GlobalFieldLabel>
              <CustomTextBox
                type="multiline"
                rules={{
                  ...register("reason", {
                    required: "Reason is required",
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else if (value?.trim().length > 255 && value !== "") {
                          return "Maximum length is 255 characters";
                        } else {
                          return true;
                        }
                      },
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (atleastOneCharacterWithSpace.test(value) || value === "") {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                selectedValue={watch("reason")}
                updateDataFunction={(event) => {
                  if (event.target.value.length <= 1024) {
                    setValue("reason", event.target.value);
                  }
                }}
                placeHolder="Provide a reason for overtime"
                errorMessage={errors?.reason?.message || ""}
                textBoxError={Boolean(errors?.reason)}
              />
            </Box>
          </Box>
        </Box>
        <Mui.Box
          sx={{
            px: 3,
            py: 1.25,
            borderTop: `1px solid ${designColors.slate200}`,
            backgroundColor: designColors.white,
          }}
        >
          <Mui.Stack direction="row" spacing={1.25} justifyContent="flex-end">
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton
              type="submit"
              onClick={handleSubmit(onSubmit)}
              loading={Boolean(watch("button_loader"))}
              disabled={Boolean(watch("button_loader"))}
              sx={{ minWidth: FORM_ACTION_BUTTON_WIDTH }}
            >
              {hasApproverAccess ? "Approve" : "Apply"}
            </PrimaryButton>
          </Mui.Stack>
        </Mui.Box>
      </Mui.Box>
    </Box>
  );

  if (expanded) {
    return createPortal(
      <Box
        sx={{
          position: "fixed",
          top: APP_BAR_OFFSET,
          left: `${contentLeft}px`,
          right: 0,
          bottom: 0,
          zIndex: 10,
          overflow: "hidden",
          backgroundColor: designColors.pageBg,
          p: 2,
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ height: "100%", minHeight: "100%", width: "100%" }}>
          {panelContent}
        </Box>
      </Box>,
      document.body,
    );
  }

  return (
    <Mui.Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={panelDrawerSx()}
    >
      <Box sx={{ width: PANEL_DRAWER_WIDTH, maxWidth: "100%", height: "100%" }}>
        {panelContent}
      </Box>
    </Mui.Drawer>
  );
};
